import { faEdit, faTrashAlt, faXmark } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  Box,
  Button,
  ErrorModal,
  Flex,
  Text,
  TextButton,
  Toast,
  useTabLeaveGuard,
} from '@modusign/design-system';
import { useUpdateLabel } from '@modusign/openapi-client-category';
import { useState } from 'react';
import { useToggle } from 'react-use';

import LabelBasicInfoForm from '@app/features/label/components/LabelBasicInfoForm';
import {
  useInvalidateLabel,
  useInvalidateLabelList,
} from '@app/features/label/hooks/useInvalidateLabelList';
import useLabelBasicInfoForm from '@app/features/label/hooks/useLabelBasicInfoForm';
import useResourceLabel from '@app/features/label/hooks/useResourceLabel';
import validateName from '@app/features/label/libs/validateName';
import { useWorkspaceId } from '@app/hooks/useWorkspaceId';

import { LabelInfoModalFooter } from '../../LabelInfoModalContent';
import LabelDeleteConfirmModal from './LabelDeleteConfirmModal';

interface LabelBasicInfoPanelProps {
  id: string;
  onBack: () => void;
}

export default function LabelBasicInfoPanel({ id, onBack }: LabelBasicInfoPanelProps) {
  const [isEditing, setIsEditing] = useState(false);

  useTabLeaveGuard(() => {
    setIsEditing(false);
    return true;
  });

  if (isEditing) {
    return <LabelBasicInfoEditor id={id} onEditingFinished={() => setIsEditing(false)} />;
  }

  return <LabelBasicInfoViewer id={id} onBack={onBack} onEdit={() => setIsEditing(true)} />;
}

interface LabelBasicInfoViewerProps {
  id: string;
  onEdit: () => void;
  onBack: () => void;
}

function LabelBasicInfoViewer({ id, onEdit, onBack }: LabelBasicInfoViewerProps) {
  const { data } = useResourceLabel(id);
  // 로딩 처리는 상위 컴포넌트에서 진행
  // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
  const label = data!;

  const [isDeleteModalOpen, toggleDeleteModalOpen] = useToggle(false);

  return (
    <Box borderBottom="1px solid" borderBottomColor="dividerNeutralSlightlyLight">
      <Flex
        borderBottom="2px solid"
        borderBottomColor="dividerNeutralBase"
        display={['none', null, 'flex']}
        justifyContent="space-between"
        pb={4}
        px={4}
      >
        <Text color="textNeutralLight" fontWeight="medium">
          관리
        </Text>
        <Flex gap={4}>
          <TextButton textDecoration="plain" onClick={toggleDeleteModalOpen}>
            <FontAwesomeIcon icon={faTrashAlt} style={{ marginRight: '2px' }} />
            삭제
          </TextButton>
          <TextButton textDecoration="plain" onClick={onEdit}>
            <FontAwesomeIcon icon={faEdit} style={{ marginRight: '2px' }} />
            수정
          </TextButton>
        </Flex>
        {isDeleteModalOpen && (
          <LabelDeleteConfirmModal
            id={id}
            onClose={() => toggleDeleteModalOpen(false)}
            onDeleted={onBack}
          />
        )}
      </Flex>
      <Flex borderBottom="1px solid" borderBottomColor="dividerNeutralSlightlyLight" px={4} py={7}>
        <Text fontWeight="medium" sx={{ width: '80px' }}>
          이름
        </Text>
        <Text>{label.name}</Text>
      </Flex>
      <Flex px={4} py={7}>
        <Text fontWeight="medium" sx={{ width: '80px' }}>
          설명
        </Text>
        <Text as="p">{label.description}</Text>
      </Flex>
    </Box>
  );
}

interface LabelBasicInfoEditorProps {
  id: string;
  onEditingFinished: () => void;
}

function LabelBasicInfoEditor({ id, onEditingFinished }: LabelBasicInfoEditorProps) {
  const workspaceId = useWorkspaceId();
  const { data } = useResourceLabel(id);
  // 로딩 처리는 상위 컴포넌트에서 진행
  // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
  const label = data!;

  const { description, isDuplicated, name, nameErrorMessage, onDescriptionChange, onNameChange } =
    useLabelBasicInfoForm({
      initialDescription: label.description || '',
      initialName: label.name,
      labelId: label.id,
    });

  const { mutate, isLoading, error, reset } = useUpdateLabel<Error>();

  const invalidateLabels = useInvalidateLabelList();
  const invalidateLabel = useInvalidateLabel();

  return (
    <Box borderBottom="1px solid" borderBottomColor="dividerNeutralSlightlyLight">
      <Flex
        borderBottom="2px solid"
        borderBottomColor="dividerNeutralBase"
        justifyContent="space-between"
        pb={4}
        px={4}
      >
        <Text color="textNeutralLight" fontWeight="medium">
          관리
        </Text>
        <TextButton textDecoration="plain" onClick={onEditingFinished}>
          <FontAwesomeIcon icon={faXmark} />
          수정 취소
        </TextButton>
      </Flex>
      <Box py={6}>
        <LabelBasicInfoForm
          description={description}
          isDuplicated={isDuplicated}
          name={name}
          nameErrorMessage={nameErrorMessage}
          onDescriptionChange={onDescriptionChange}
          onNameChange={onNameChange}
        />
      </Box>
      <LabelInfoModalFooter gap={3} justifyContent="flex-end">
        <Button disabled={isLoading} variant="subtle" weight="light" onClick={onEditingFinished}>
          취소
        </Button>
        <Button
          isLoading={isLoading}
          type="submit"
          variant="primary"
          weight="light"
          onClick={() => {
            switch (validateName(name.trim())) {
              case 'EMPTY':
                Toast.warning({ description: '라벨 이름을 입력해 주세요.' });
                return;
              case 'INVALID':
                Toast.warning({ description: '올바른 형식의 라벨 이름을 입력해 주세요.' });
                return;
              default:
            }

            mutate(
              {
                labelId: id,
                workspaceId,
                data: {
                  name: name.trim(),
                  description,
                },
              },
              {
                onSuccess: () => {
                  invalidateLabels();
                  invalidateLabel(id);
                  onEditingFinished();
                },
              }
            );
          }}
        >
          적용
        </Button>
        <ErrorModal error={error} isOpen title="수정 실패" onClose={() => reset()} />
      </LabelInfoModalFooter>
    </Box>
  );
}
